/**
 * ItemDetailModal.jsx — Reusable item-analysis modal.
 *
 * Mounted once at the app root (see main.jsx). Listens for the
 * `v2-item-pick` CustomEvent (detail.name) — any widget that wants to
 * surface the analysis modal just dispatches that event on `window`.
 *
 * Pulls the same `item-detail` widget endpoint used by ItemDetail.jsx
 * so the data shape is consistent.
 */

import { createSignal, createEffect, Show, For, onCleanup, onMount } from 'solid-js';
import { Portal } from 'solid-js/web';
import api from '../lib/apiClient.js';
import wm from '../lib/wmStore.js';
import { toast } from '../lib/toastStore.js';
import { gold, goldPerEach, qty, relTime, qualityColor, esoIconUrl, ESO_ICON_FALLBACK, freqLabel } from '../lib/format.js';
import FissalSuggestion from '../components/FissalSuggestion.jsx';
import { pickUser, pickGuild } from '../lib/picks.js';
import './v2-modal.css';
import './ItemDetailModal.css';
import SellingTips from '../components/SellingTips.jsx';

export default function ItemDetailModal() {
  const [name, setName] = createSignal(null);
  // Icon hinted by the originating click (#89). The detail endpoint
  // resolves item by name and returns the icon of the first MM_Items
  // row that matches — for variant items that's not necessarily the
  // row the user clicked. Showing the click's icon (when supplied)
  // keeps the popup visually consistent with the source row.
  const [iconHint, setIconHint] = createSignal(null);
  const [data, setData] = createSignal(null);
  const [loading, setLoading] = createSignal(false);
  const [error, setError] = createSignal(null);

  function open(n, hint) {
    if (!n) return;
    setName(n);
    setIconHint(hint || null);
    setData(null);
    setError(null);
  }
  function close() {
    setName(null);
    setIconHint(null);
    setData(null);
    setError(null);
  }

  // Fetch on name change.
  createEffect(() => {
    const n = name();
    if (!n) return;
    setLoading(true);
    api.fetchWidget('item-detail', { name: n })
      .then((r) => { setData(r?.payload || null); setError(null); })
      .catch((e) => setError(e.message || 'Failed to load.'))
      .finally(() => setLoading(false));
  });

  function onPick(ev) { open(ev?.detail?.name, ev?.detail?.icon || null); }
  function onKey(ev) { if (ev.key === 'Escape') close(); }
  onMount(() => {
    window.addEventListener('v2-item-pick', onPick);
    window.addEventListener('keydown', onKey);
  });
  onCleanup(() => {
    window.removeEventListener('v2-item-pick', onPick);
    window.removeEventListener('keydown', onKey);
  });

  // Issue #92 — promote the popup into a transient session window using
  // the existing item-detail widget renderer (so the visual layer is
  // identical to the popup body). Settings carry the search name; the
  // widget's ItemSearchInput pre-populates from `settings.name`.
  function promoteToWindow() {
    const id = wm.addTransientWidget('item-detail', { name: name() || '' });
    if (id) {
      toast.info('Pinned to dashboard', { ttl: 1400 });
      close();
    }
  }

  return (
    <Show when={name()}>
      <Portal>
        <div class="v2-modal__backdrop" onClick={close}>
          <div class="v2-modal" onClick={(e) => e.stopPropagation()}>
            <div class="v2-modal__actions">
              <button
                class="v2-btn-sm v2-modal__expand"
                onClick={promoteToWindow}
                title="📌 Pin to dashboard"
                aria-label="Pin to dashboard"
              >📌</button>
              <button class="v2-btn-sm" onClick={close} title="Close (Esc)" aria-label="Close">✖</button>
            </div>

            <Show when={loading() && !data()}>
              <div class="v2-widget-skel">Analyzing…</div>
            </Show>
            <Show when={error()}>
              <div class="v2-error">{error()}</div>
            </Show>
            <Show when={data() && !data().item && !loading()}>
              <div class="v2-empty">No data for that item.</div>
            </Show>

            <Show when={data()?.item}>
              {(() => {
                const item = data().item;
                const rf = item.rf || null;
                const ttc = item.ttc || null;
                const fissal = data().fissal || null;
                const recent = (rf?.sales || []).slice(0, 12);
                return (
                  <>
                    <div class="v2-modal__head">
                      <img class="v2-item-modal__icon"
                        src={iconHint() ? esoIconUrl(iconHint()) : (item.icon ? esoIconUrl(item.icon) : ESO_ICON_FALLBACK)}
                        alt=""
                        onError={(e) => { e.currentTarget.src = ESO_ICON_FALLBACK; }} />
                      <div class="v2-modal__head-text">
                        <div
                          class="v2-modal__name v2-modal__name--item"
                          style={{ '--item-quality': qualityColor(item.quality) }}
                        >
                          {rf?.actualItemName || item.name || name()}
                        </div>
                        <div class="v2-modal__sub">
                          <Show when={item.quality}><span class="v2-quality-dot" style={{ background: qualityColor(item.quality) }} title={item.quality} /> {item.quality}</Show>
                          <Show when={item.trait}><span class="v2-trait-badge">{item.trait}</span></Show>
                        </div>
                      </div>
                    </div>

                    <div class="v2-stat-grid">
                      <Show when={rf?.avgPrice}>
                        <div class="v2-stat v2-stat--gold">
                          <span class="v2-stat__label">MM avg</span>
                          <span class="v2-stat__value">{gold(rf.avgPrice)}</span>
                          <span class="v2-stat__sub">{qty(rf.saleCount)} sales · {qty(rf.volumeCount)} items</span>
                        </div>
                      </Show>
                      <Show when={ttc?.avgPrice}>
                        <div class="v2-stat v2-stat--cyan">
                          <span class="v2-stat__label">TTC avg</span>
                          <span class="v2-stat__value">{gold(ttc.avgPrice)}</span>
                          <span class="v2-stat__sub">{qty(ttc.count || ttc.saleCount || 0)} listings</span>
                        </div>
                      </Show>
                      <Show when={rf?.minPrice}>
                        <div class="v2-stat">
                          <span class="v2-stat__label">Range</span>
                          <span class="v2-stat__value v2-stat__value--small">
                            {gold(rf.minPrice)}<br />
                            <span class="v2-stat__range-max">↑ {gold(rf.maxPrice)}</span>
                          </span>
                        </div>
                      </Show>
                      <Show when={rf?.sellFrequency}>
                        <div class="v2-stat">
                          <span class="v2-stat__label">Frequency</span>
                          <span class="v2-stat__value v2-stat__value--small">{freqLabel(rf.sellFrequency)}</span>
                        </div>
                      </Show>
                    </div>

                    <Show when={fissal?.fissalSuggested}>
                      <FissalSuggestion fissal={fissal} compact />
                    </Show>

                    <SellingTips tips={rf?.sellingTips} />

                    <Show when={recent.length}>
                      <div class="v2-section-h">Recent sales</div>
                      <div class="v2-modal__sales">
                        <For each={recent}>{(s) => (
                          <div class="v2-sale-row">
                            <span class="v2-sale-row__time">{relTime(s.timestamp)}</span>
                            <span class="v2-sale-row__main">
                              <Show when={s.sellerName || s.seller} fallback={<span class="v2-dim">(unknown)</span>}>
                                <b class="v2-pick v2-pick--user" onClick={() => pickUser(s.sellerName || s.seller)}>{s.sellerName || s.seller}</b>
                              </Show>
                              <Show when={s.quant > 1}><em> ×{s.quant}</em></Show>
                              <Show when={s.guildName}><em class="v2-sale-row__guild"> · <span class="v2-pick v2-pick--guild" onClick={() => pickGuild(s.guildName)}>{s.guildName}</span></em></Show>
                            </span>
                            <span class="v2-sale-row__price">
                              {gold(s.price)}
                              <Show when={s.quant > 1}>
                                <em class="v2-sale-row__per"> {goldPerEach(s.price, s.quant)}</em>
                              </Show>
                            </span>
                          </div>
                        )}</For>
                      </div>
                    </Show>
                  </>
                );
              })()}
            </Show>
          </div>
        </div>
      </Portal>
    </Show>
  );
}
